/**
 * @internal
 *
 * `_createOwnedSignal` — compile-time escape hatch emitted by Phase 7 of
 * vite-plugin-flexium (`use-substitution` pass). Never call from handwritten
 * application code; use `use(initialValue)` instead. This module is exposed
 * at the package sub-path `flexium/core/owned-signal` so the compiler-emitted
 * import does not depend on `flexium`'s public surface.
 *
 * Semantics:
 *   1. Creates a V2 signal directly via `createSignalV2` — no hook bookkeeping.
 *   2. If invoked inside a component render, registers a cleanup hook with the
 *      Phase 4 componentRegistry so the signal's subscriber list is cleared on
 *      unmount (or via FinalizationRegistry GC as a backstop).
 *   3. Returns `[getter, setter]` — same tuple shape as `createSignal()`. The
 *      compiler emits an immediate `[0]()` call to recover the value form:
 *
 *        const __sig0 = _createOwnedSignal(0);
 *        const count   = __sig0[0]();
 *        const setCount = __sig0[1];
 *
 *   4. Outside a component context (top-level module, SSR top-level, raw
 *      tests) the signal is unowned — same fallback as `createSignal()`.
 *
 * V1/V2 compatibility:
 *   Always uses `createSignalV2` regardless of the `FLEXIUM_REACTIVITY_V2`
 *   flag. The compile-time substitution path is intentionally V2-only; when
 *   V1 mode is active, the vite-plugin-flexium optimizer is typically off
 *   and userland code stays on the runtime `use()` fallback path.
 */

import { createSignalV2 } from './reactive-v2'
import { scheduleEffect } from './scheduler'
import { getComponent } from './hook'
import { componentRegistry } from './component-registry'
import type { Setter } from './use'

/**
 * Module-scope counter for unique per-signal registry keys.
 *
 * Each registered owned signal gets its own `__owned:N` key so multiple signals
 * within the same component each have independent cleanup entries. The counter
 * never resets across the lifetime of a dev server — that is correct (uniqueness
 * is monotonic) and the integer is bounded by JS number precision (2^53 entries
 * is unreachable in practice).
 */
let _ownedSignalIdCounter = 0

/**
 * @internal — called only by vite-plugin-flexium Phase 7 compile output.
 *
 * Creates a component-owned signal whose effect subscribers are cleared when
 * the owning component unmounts. Outside a component, behaves like
 * `createSignal()`.
 */
export function _createOwnedSignal<T>(
    initialValue: T
): [() => T, Setter<T>] {
    const sc = createSignalV2(initialValue, scheduleEffect)
    const getter = sc._getter
    // SignalContainerV2._setter already handles functional updates + Object.is
    // dedup + trigger internally (Phase 5/6) — exposing it directly avoids a
    // wrapping closure allocation per signal.
    const setter = sc._setter as Setter<T>

    const component = getComponent()
    if (component) {
        const key = `__owned:${_ownedSignalIdCounter++}`
        componentRegistry.register(component, key, () => {
            // Bidirectional unlink: walk each subscriber's `deps` array,
            // remove this signal so the effect won't see a dangling pointer
            // after the component is gone. Then clear the signal's subscriber
            // list so any future triggerV2() finds an empty array.
            const container = sc._v2Container
            const effs = container.effects
            for (let i = 0; i < effs.length; i++) {
                const eff = effs[i]
                if (eff && eff.deps) {
                    const idx = eff.deps.indexOf(container)
                    if (idx !== -1) eff.deps.splice(idx, 1)
                }
            }
            effs.length = 0
        })
    }

    return [getter, setter]
}

/** @internal — test helper to reset the per-signal counter between tests. */
export function __resetOwnedSignalCounterForTest(): void {
    _ownedSignalIdCounter = 0
}
